/**
 * Display-name normalisation for entity cells.
 *
 * Raw names from registries are inconsistent — `ACME HOLDINGS LIMITED`,
 * `MR JOHN SMITH`, `SMITH, JANE P.`. These helpers produce a human-friendly
 * label without changing the DB representation. Use at render time only; the
 * search layer still hits `normalized_name` on the backend.
 *
 * Scope is intentionally pragmatic, not exhaustive. Known limitations:
 *   - Acronyms shorter than the company-uppercase whitelist (e.g. "IBM") will
 *     be title-cased unless added to COMPANY_UPPERCASE.
 *   - Cultural name particles ("van", "della", "bin") are not special-cased.
 */

const PERSON_TITLES = new Set([
  'MR', 'MRS', 'MS', 'MISS', 'DR', 'PROF', 'PROFESSOR',
  'SIR', 'DAME', 'LORD', 'LADY', 'HON', 'HONOURABLE', 'HONORABLE',
  'CAPT', 'CAPTAIN', 'REV', 'REVEREND', 'FR', 'FATHER',
]);

// Lowercased inside a name (excluding when they're the first word).
const LOWERCASE_CONNECTORS = new Set([
  'of', 'the', 'and', 'for', 'on', 'in', 'at', 'by', 'to',
]);

// Company suffixes / acronyms kept all-caps.
const COMPANY_UPPERCASE = new Set([
  'LTD', 'LTDA', 'LLC', 'LLP', 'PLC', 'INC', 'INCORPORATED',
  'CO', 'CORP', 'CORPORATION', 'SA', 'NV', 'AG', 'GMBH', 'BV',
  'KG', 'AS', 'AB', 'OY', 'OYJ', 'SPA', 'SRL', 'SARL', 'UAB', 'UK', 'USA',
  'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X',
  'UK', 'US', 'EU', 'NYSE', 'AIM',
]);

/**
 * Title-case a single whitespace-delimited token. Handles Mc/Mac/O' prefixes
 * and hyphenated / apostrophised subwords. `isFirstWord` suppresses the
 * connector-lowercase rule so "The Red Wood" doesn't become "the Red Wood".
 */
function titleCaseWord(word: string, isFirstWord: boolean): string {
  if (!word) return word;
  const upper = word.toUpperCase();
  if (COMPANY_UPPERCASE.has(upper)) return upper;

  // If it's a single letter, keep it uppercase ("H & M" → "H & M").
  if (word.length === 1) return upper;

  const lower = word.toLowerCase();
  if (!isFirstWord && LOWERCASE_CONNECTORS.has(lower)) return lower;

  // Compound separators: hyphen and apostrophe. Recurse on each segment.
  if (word.includes('-')) {
    return word.split('-').map((seg, i) => titleCaseWord(seg, isFirstWord && i === 0)).join('-');
  }
  // "O'BRIEN" → "O'Brien"
  if (/^o'/i.test(word) && word.length > 2) {
    return "O'" + titleCaseWord(word.slice(2), true);
  }
  // "MCLAUGHLIN" → "McLaughlin" (but not "MC" alone).
  if (/^mc/i.test(word) && word.length > 2) {
    return 'Mc' + titleCaseWord(word.slice(2), true);
  }
  // "MACDONALD" — heuristic: "Mac" + at-least-three-letter-start like "don" / "leo".
  if (/^mac[a-z]/i.test(word) && word.length > 4) {
    return 'Mac' + titleCaseWord(word.slice(3), true);
  }

  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

function titleCaseString(s: string): string {
  return s.split(/\s+/).map((w, i) => titleCaseWord(w, i === 0)).join(' ');
}

export function normalizeCompanyName(raw: string | null | undefined): string {
  if (!raw) return '';
  return titleCaseString(raw.trim().replace(/\s+/g, ' '));
}

export function normalizePersonName(raw: string | null | undefined): string {
  if (!raw) return '';
  let s = raw.trim().replace(/\s+/g, ' ');

  // "SMITH, JANE P." → "JANE P. SMITH"
  if (s.includes(',')) {
    const idx = s.indexOf(',');
    const last = s.slice(0, idx).trim();
    const rest = s.slice(idx + 1).trim();
    if (last && rest) s = `${rest} ${last}`;
  }

  const tokens = s.split(/\s+/);
  while (tokens.length > 1) {
    const head = tokens[0].replace(/\.$/, '').toUpperCase();
    if (PERSON_TITLES.has(head)) {
      tokens.shift();
    } else {
      break;
    }
  }

  return titleCaseString(tokens.join(' '));
}
